/*
 * Wolense — weekly reading-capacity counter
 * ------------------------------------------------------------------
 * Fills three things from ONE shared config:
 *   1. the top bar / buy-box card  [data-wolense-capacity]
 *   2. its Days:Hrs:Mins:Secs clock
 *   3. any inline [spots] or [cap] token in text anywhere on the site
 *
 * The config comes from the shop metafields via
 * snippets/wolense-capacity-engine.liquid, so every surface shows the
 * same figure by construction rather than by two settings being kept
 * in step by hand.
 *
 * Design notes:
 *  - The curve is DETERMINISTIC, seeded from the date the current cycle
 *    starts. Every visitor, every device, every refresh sees the same
 *    number at the same moment. Per-visitor randomness is how counters
 *    like this get caught out.
 *  - A cycle always opens at `cap` and always lands on `minLeft`
 *    (default 1). It never reaches zero unless the floor is set to zero.
 *  - A cycle normally runs Monday 00:00 -> Monday 00:00. If data-from
 *    names a date inside the current week, the cycle instead runs from
 *    that day's midnight to the Sunday close — which is how a mid-week
 *    cap change restarts the descent from the new number.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* The asset may be included by more than one section on a page. */
  if (window.WolenseCapacity) return;

  var DAY = 86400000;
  var WEEKDAY = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /* ---------- deterministic PRNG (mulberry32) ---------------------- */
  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /* ---------- timezone helpers ------------------------------------- */
  var dtfCache = {};

  function formatter(tz) {
    if (!dtfCache[tz]) {
      dtfCache[tz] = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false, weekday: 'short',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    }
    return dtfCache[tz];
  }

  function zoned(ts, tz) {
    var parts = {};
    try {
      formatter(tz).formatToParts(new Date(ts)).forEach(function (p) { parts[p.type] = p.value; });
    } catch (e) {
      var d = new Date(ts);
      return {
        y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate(),
        H: d.getHours(), M: d.getMinutes(), S: d.getSeconds(), wd: d.getDay()
      };
    }
    return {
      y: +parts.year, m: +parts.month, d: +parts.day,
      H: parts.hour === '24' ? 0 : +parts.hour,
      M: +parts.minute, S: +parts.second, wd: WEEKDAY[parts.weekday]
    };
  }

  /* Instant of Monday 00:00:00 local-to-tz for the week containing ts. */
  function weekStart(ts, tz) {
    var t = ts;
    for (var i = 0; i < 4; i++) {
      var z = zoned(t, tz);
      var into = (((z.wd + 6) % 7) * 24 * 3600 + z.H * 3600 + z.M * 60 + z.S) * 1000;
      if (into === 0) break;
      t -= into;
    }
    return t;
  }

  /* Instant of 00:00:00 local-to-tz on a given calendar date. */
  function dayStart(y, m, d, tz) {
    var t = Date.UTC(y, m - 1, d, 0, 0, 0);
    for (var i = 0; i < 4; i++) {
      var z = zoned(t, tz);
      var err = (Date.UTC(z.y, z.m - 1, z.d) - Date.UTC(y, m - 1, d)) +
                (z.H * 3600 + z.M * 60 + z.S) * 1000;
      if (err === 0) break;
      t -= err;
    }
    return t;
  }

  /* "2026-07-30" -> instant, or null. */
  function parseDate(str, tz) {
    var mm = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})\s*$/.exec(String(str || ''));
    if (!mm) return null;
    return dayStart(+mm[1], +mm[2], +mm[3], tz);
  }

  /* ---------- hour-of-day shape ------------------------------------
   * Shaped like real US ecommerce order volume: quiet 03:00–06:00,
   * climbing through the morning, peaking 20:00–22:00. No hour is ever
   * zero, so the counter can move at any time of day or night.
   * -------------------------------------------------------------- */
  var HOUR_WEIGHTS = [
    1.5, 0.9, 0.6, 0.4, 0.4, 0.5,
    0.9, 1.6, 2.4, 3.0, 3.4, 3.5,
    3.4, 3.3, 3.3, 3.4, 3.4, 3.5,
    3.8, 4.4, 5.0, 5.2, 4.4, 2.8
  ];

  var HOUR_CDF = (function () {
    var c = [], sum = 0, i;
    for (i = 0; i < 24; i++) { sum += HOUR_WEIGHTS[i]; c[i] = sum; }
    for (i = 0; i < 24; i++) { c[i] /= sum; }
    return c;
  })();

  function sampleHour(rnd) {
    var u = rnd();
    for (var i = 0; i < 24; i++) { if (u <= HOUR_CDF[i]) return i + rnd(); }
    return 23 + rnd();
  }

  /* ---------- the decay schedule -----------------------------------
   * Ascending offsets from the cycle start, one per spot given away.
   * Exactly (cap - minLeft) of them, so the cycle lands on minLeft.
   * -------------------------------------------------------------- */
  function schedule(seed, cap, days, minLeft) {
    var totalDrop = Math.max(0, cap - minLeft);
    if (totalDrop === 0) return [];

    var rnd = mulberry32(seed);
    var n = Math.max(1, days);
    var i, k;

    /* When there are fewer spots than days, some days simply stay quiet.
       Forcing one per day would consume more than the cap and bottom the
       counter out early. */
    var minPerDay = totalDrop >= n ? 1 : 0;

    var w = [], sum = 0;
    for (i = 0; i < n; i++) { w[i] = 0.25 + rnd() * 1.75; sum += w[i]; }

    var q = [], acc = 0;
    for (i = 0; i < n; i++) {
      q[i] = Math.max(minPerDay, Math.floor((w[i] / sum) * totalDrop));
      acc += q[i];
    }

    var guard = 0;
    while (acc !== totalDrop && guard++ < 2000) {
      var j = Math.floor(rnd() * n);
      if (acc < totalDrop) { q[j]++; acc++; }
      else if (q[j] > minPerDay) { q[j]--; acc--; }
    }

    var times = [];
    for (i = 0; i < n; i++) {
      for (k = 0; k < q[i]; k++) { times.push(i * DAY + sampleHour(rnd) * 3600000); }
    }
    times.sort(function (a, b) { return a - b; });

    /* Land the final spot on the last evening, so the cycle doesn't sit
       on its floor for most of the closing day. */
    times[times.length - 1] = (n - 1) * DAY + (18 + rnd() * 4.5) * 3600000;
    times.sort(function (a, b) { return a - b; });
    return times;
  }

  /* ---------- current cycle ---------------------------------------- */
  var cycleCache = {};

  function currentCycle(now, tz, cap, fromStr, minLeft) {
    var key = tz + '|' + cap + '|' + (fromStr || '') + '|' + minLeft;
    var c = cycleCache[key];
    if (c && now >= c.cacheFrom && now < c.closesAt) return c;

    var ws = weekStart(now, tz);
    /* true start of next week — not ws + 7*DAY, which overshoots by an
       hour on the spring-forward week and desyncs timer from counter */
    var closesAt = weekStart(ws + 7 * DAY + DAY / 2, tz);

    /* A date inside the current week restarts the descent from that day. */
    var start = ws;
    var from = parseDate(fromStr, tz);
    if (from !== null && from > ws && from < closesAt) start = from;

    var days = Math.max(1, Math.round((closesAt - start) / DAY));
    var z = zoned(start, tz);

    c = {
      start: start,
      closesAt: closesAt,
      cacheFrom: ws,
      times: schedule(z.y * 10000 + z.m * 100 + z.d, cap, days, minLeft)
    };
    cycleCache[key] = c;
    return c;
  }

  function computeState(now, tz, cap, fromStr, minLeft) {
    minLeft = (minLeft === undefined || minLeft === null) ? 1 : minLeft;
    var cy = currentCycle(now, tz, cap, fromStr, minLeft);
    var times = cy.times;
    var elapsed = now - cy.start;
    var consumed = 0;
    for (var i = 0; i < times.length; i++) {
      if (times[i] <= elapsed) consumed++; else break;
    }
    return {
      left: Math.max(minLeft, cap - consumed),
      cap: cap,
      msLeft: Math.max(0, cy.closesAt - now)
    };
  }

  /* ---------- formatting ------------------------------------------- */
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function formatTimer(ms) {
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    if (d > 0) return d + 'd ' + h + 'h';
    if (h > 0) return h + 'h ' + pad(m) + 'm';
    return pad(m) + 'm ' + pad(s) + 's';
  }

  function timerParts(ms) {
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    return { d: pad(d), h: pad(h), m: pad(m), s: pad(s) };
  }

  /* Placeholders are [spots] / [cap] / [timer], NOT {{spots}} — Shopify
     parses {{ }} in a section-setting default as a dynamic metafield
     source and refuses to save the theme. Both forms accepted. */
  function fill(tpl, state) {
    return String(tpl || '')
      .replace(/\[\s*spots\s*\]|\{\{\s*spots\s*\}\}/g, state.left)
      .replace(/\[\s*cap\s*\]|\{\{\s*cap\s*\}\}/g, state.cap)
      .replace(/\[\s*timer\s*\]|\{\{\s*timer\s*\}\}/g, formatTimer(state.msLeft));
  }

  function copyFor(el, state) {
    var d = el.dataset;
    if (state.left <= 0) return d.copyFull;
    if (state.left === 1) return d.copyLast;
    if (state.left <= 5) return d.copyLow;
    if (state.left <= 19) return d.copyMid;
    return d.copyHigh;
  }

  /* ---------- height broadcast -------------------------------------
   * The theme header is pushed down by exactly the bar's height, and
   * has to travel that far extra when it hides. Publishing the measured
   * height as a CSS variable keeps that correct if the copy ever wraps
   * to two lines, or at any viewport width — nothing is hardcoded.
   * -------------------------------------------------------------- */
  var lastH = null;

  function syncHeight() {
    var bar = document.querySelector('.wl-cap--sticky:not([hidden])');
    if (!bar) return;
    var h = bar.getBoundingClientRect().height;
    if (!h || h === lastH) return;
    lastH = h;
    document.documentElement.style.setProperty('--wl-cap-h', h.toFixed(2) + 'px');
  }

  /* ---------- shared config ----------------------------------------
   * One source of truth for the whole store, rendered from the shop
   * metafields by snippets/wolense-capacity-engine.liquid.
   * -------------------------------------------------------------- */
  function config() {
    var el = document.querySelector('[data-wolense-capacity-config]') ||
             document.querySelector('[data-wolense-capacity]');
    var d = el ? el.dataset : {};
    return {
      cap: parseInt(d.cap, 10) || 30,
      tz: d.tz || 'America/New_York',
      from: d.from || '',
      min: d.min === undefined ? 1 : (parseInt(d.min, 10) || 0)
    };
  }

  /* ---------- inline [spots] / [cap] tokens -------------------------
   * Any text anywhere on the site can carry [spots] or [cap]. We swap
   * each token for a span ONCE, then only rewrite those spans on each
   * tick — walking the document every second would be wasteful.
   * -------------------------------------------------------------- */
  var TOKEN = /\[\s*(spots|cap)\s*\]/g;
  var SKIP = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, NOSCRIPT: 1, OPTION: 1 };

  function wrapTokens() {
    if (!document.body) return;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        var v = n.nodeValue;
        if (!v || v.indexOf('[') === -1) return NodeFilter.FILTER_REJECT;
        TOKEN.lastIndex = 0;
        if (!TOKEN.test(v)) return NodeFilter.FILTER_REJECT;
        var p = n.parentNode;
        if (!p || SKIP[p.nodeName]) return NodeFilter.FILTER_REJECT;
        /* the bar and card render their own copy from data-copy-* */
        if (p.closest && p.closest('[data-wolense-capacity]')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    var found = [], n;
    while ((n = walker.nextNode())) found.push(n);

    found.forEach(function (node) {
      var v = node.nodeValue, frag = document.createDocumentFragment(), last = 0, m;
      TOKEN.lastIndex = 0;
      while ((m = TOKEN.exec(v))) {
        if (m.index > last) frag.appendChild(document.createTextNode(v.slice(last, m.index)));
        var span = document.createElement('span');
        span.setAttribute(m[1] === 'spots' ? 'data-wl-spots' : 'data-wl-cap', '');
        frag.appendChild(span);
        last = m.index + m[0].length;
      }
      if (last < v.length) frag.appendChild(document.createTextNode(v.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
  }

  function paintTokens(state) {
    var a = document.querySelectorAll('[data-wl-spots]'), i;
    for (i = 0; i < a.length; i++) {
      if (a[i].textContent !== String(state.left)) a[i].textContent = state.left;
    }
    var b = document.querySelectorAll('[data-wl-cap]');
    for (i = 0; i < b.length; i++) {
      if (b[i].textContent !== String(state.cap)) b[i].textContent = state.cap;
    }
  }

  /* ---------- render loop ------------------------------------------ */
  var SEG_KEYS = ['d', 'h', 'm', 's'];

  function paint() {
    var now = Date.now();
    var cfg = config();
    var state = computeState(now, cfg.tz, cfg.cap, cfg.from, cfg.min);

    paintTokens(state);

    var nodes = document.querySelectorAll('[data-wolense-capacity]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];

      var textEl = el.querySelector('[data-capacity-text]');
      if (textEl) {
        var next = fill(copyFor(el, state), state);
        if (textEl.innerHTML !== next) textEl.innerHTML = next;
      }

      var timerEl = el.querySelector('[data-capacity-timer]');
      if (timerEl) timerEl.textContent = formatTimer(state.msLeft);

      var seg = timerParts(state.msLeft);
      for (var k = 0; k < SEG_KEYS.length; k++) {
        var segEl = el.querySelector('[data-t-' + SEG_KEYS[k] + ']');
        if (segEl && segEl.textContent !== seg[SEG_KEYS[k]]) {
          segEl.textContent = seg[SEG_KEYS[k]];
        }
      }

      el.setAttribute('data-state', state.left <= 0 ? 'full' : state.left <= 5 ? 'low' : 'open');
      if (el.hasAttribute('hidden')) el.removeAttribute('hidden');
    }

    syncHeight();
  }

  function start() {
    wrapTokens();
    paint();
    setInterval(paint, 1000);
    addEventListener('resize', function () { lastH = null; syncHeight(); }, { passive: true });

    /* Landing pages render some blocks late (page builders, lazy sections),
       so sweep for tokens a couple more times rather than only at ready. */
    [800, 2500, 6000].forEach(function (ms) {
      setTimeout(function () { wrapTokens(); paint(); }, ms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.WolenseCapacity = {
    config: config,
    wrapTokens: wrapTokens,
    computeState: computeState,
    schedule: schedule,
    weekStart: weekStart,
    dayStart: dayStart,
    formatTimer: formatTimer,
    timerParts: timerParts
  };
})();
